<template>
    <article>
        <p>
            These memory inspections identify problems with the application's memory:
        </p>
        <ul>
            <li>
                Out of Memory: detects situations where an application's container has been terminated by the OOM (Out Of Memory) killer.
            </li>
            <li>
                Memory leak: monitors the memory usage of the app over time and detects memory leaks before the OOM killer restarts the application containers.
            </li>
        </ul>

        <v-card>
            <v-img eager src="/static/img/docs/coroot-ce/inspections/memory.png" />
        </v-card>

        <h2>Possible failure scenarios</h2>

        <h3>Out of Memory</h3>

        <p>There are several reasons why the OOM killer mechanism might terminate a process:</p>
        <ul>
            <li>
                If a container has reached its memory limit.
            </li>
            <li>
                If a top-level cgroup has reached its memory limit.
                For example, Kubernetes limits the <var>kubepods</var> cgroup to the size of allocatable memory on the node,
                so a container can be killed even if it has no memory limit.
            </li>
            <li>
                If a node has run out of memory.
            </li>
        </ul>
        <p>
            Though the terminated container will be restarted by a container runtime, this can affect the application SLIs (Services Level Indicators).
            For example, when a container is terminated, all its in-progress requests will fail.
        </p>
        <p>
            In the worst cases of node-level OOMs, a node becomes unresponsive due to the low-memory condition.
            This means that every application on the node 'freezes', so not only the OOM Killer victim's SLI can be affected.
        </p>
        <p>
            Coroot utilizes the <MetricLink name="container_oom_kills_total" exporter="node-agent"/> metric to identify which containers have been terminated.
        </p>

        <h3>Memory leak</h3>
        <p>
            Memory leaks typically occur when a program allocates memory dynamically during its execution but forgets to release it when it's no longer needed.
        </p>
        <p>
            Coroot calculates linear regression using the <MetricLink name="container_resources_memory_rss_bytes" exporter="node-agent"/> metric
            to determine if a container's memory consumption is increasing over time.
        </p>
        <p>The default threshold for detecting a significant increase in memory consumption is set at 10% per hour.</p>


        <h2>Dashboard</h2>

        <h3 id="usage">Memory usage</h3>
        <v-card max-width="550">
            <v-img eager src="/static/img/docs/coroot-ce/inspections/memory-containers.png" />
        </v-card>
        <p>This chart can help you answer the following questions:</p>
        <ul>
            <li>Are all the application instances consuming the same amount of memory, or are there any outliers?</li>
            <li>How does memory consumption change over time?</li>
            <li>Assess the memory usage of each container in comparison to its limit.</li>
        </ul>
        <p>
            Memory usage is calculated using the <MetricLink name="container_resources_memory_rss_bytes" exporter="node-agent" /> metric,
            and does not take in to account the amount of page cache memory allocated by the container.
        </p>
        <p>
            If the application Pods contain more then one container,
            this chart provides you with both per-container and total views.
        </p>
        <p>
            The <v-btn x-small color="primary" class="caption">profile</v-btn> button opens the memory profiling data,
            allowing you to identify and analyze unexpected spikes in memory usage down to the precise line of code.
        </p>
        <v-alert icon="mdi-lightbulb-on-outline" color="grey" outlined>
            Learn more about <router-link :to="{name: 'docs', params: {chapter: 'coroot', group: 'profiling'}}">Continuous profiling</router-link> in Coroot.
        </v-alert>

        <h3 id="oom">Out of memory events</h3>
        <v-card max-width="550">
            <v-img eager src="/static/img/docs/coroot-ce/inspections/memory-ooms.png" />
        </v-card>
        <p>
            Based on the <MetricLink name="container_oom_kills_total" exporter="node-agent"/> metric,
            this chart shows the number of times application containers have been terminated by the OOM killer and when these terminations occurred.
        </p>

        <h3 id="node_usage">Node memory usage (unreclaimable)</h3>
        <v-card max-width="550">
            <v-img eager src="/static/img/docs/coroot-ce/inspections/memory-nodes.png" />
        </v-card>
        <p>
            This chart allows you to estimate the memory usage of the related nodes.
            It does not take into account the page cache size because this memory can be reclaimed for new allocations.
        </p>
        <p>
            In situations where there is no memory available for allocations,
            the OOM killer will terminate certain processes on the node, even if they have no memory limits defined.
        </p>
        <v-alert icon="mdi-lightbulb-on-outline" color="grey" outlined>
            Node memory usage = (
                <MetricLink name="node_resources_memory_total_bytes" exporter="node-agent" alias="total"/> -
                <MetricLink name="node_resources_memory_available_bytes" exporter="node-agent" alias="available"/>
            ) / <MetricLink name="node_resources_memory_total_bytes" exporter="node-agent" alias="total"/> * 100%
        </v-alert>

        <h3 id="consumers">Memory consumers</h3>
        <v-row no-gutters>
            <v-col>
                <v-card max-width="550">
                    <v-img eager src="/static/img/docs/coroot-ce/inspections/memory-consumers-1.png" />
                </v-card>
            </v-col>
            <v-col>
                <v-card max-width="550">
                    <v-img eager src="/static/img/docs/coroot-ce/inspections/memory-consumers-2.png" />
                </v-card>
            </v-col>
        </v-row>
        <p>
            When you observe high memory usage on a particular node,
            this chart can assist you in identifying the primary memory-consuming applications.
            The chart displays the top 5 applications by their peak memory consumption.
        </p>
        <p>
            The chart is based on the <MetricLink name="container_resources_memory_rss_bytes" exporter="node-agent" /> metric,
            which does not include the amount of page cache allocated by the container.
        </p>
    </article>
</template>

<script>

import MetricLink from "@/components/MetricLink.vue";

export default {
    components: {MetricLink}
}
</script>

<style scoped>
</style>
